import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ChevronDown, X, Check, Plus } from 'lucide-react';
import ExitSettlementDialog from '../employees/ExitSettlementDialog';
import api from '../../services/api';

const SETTLEMENT_ACTIONS = { 'Absconding': 'absconding', 'In Prison': 'in_prison', 'Death': 'death' };

const EmploymentInfoSection = ({ 
  formData, 
  handleChange, 
  departments, 
  showAddDepartment, 
  setShowAddDepartment,
  newDepartment,
  setNewDepartment,
  setDepartments,
  designations,
  showAddDesignation,
  setShowAddDesignation,
  newDesignation,
  setNewDesignation,
  setDesignations,
  allEmployees = [],
  currentEmployeeId,
  onHidePosition,
  onHideDepartment
}) => {
  const [posOpen, setPosOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [mgrPopoverOpen, setMgrPopoverOpen] = useState(false);
  const [settleAction, setSettleAction] = useState(null);
  const [prevStatus, setPrevStatus] = useState(formData.employment_status);

  const onStatusChange = (value) => {
    const action = SETTLEMENT_ACTIONS[value];
    if (action && currentEmployeeId) {
      // Terminal status with a settlement flow — open the dialog; only commit on confirm.
      setPrevStatus(formData.employment_status);
      setSettleAction(action);
      return;
    }
    handleChange('employment_status', value);
  };

  const leadershipRoles = ['Owner', 'Top Management', 'Management', 'Manager'];
  const allManagers = allEmployees.filter(
    emp => leadershipRoles.includes(emp.employee_role)
      && emp.id !== currentEmployeeId
      && emp.employment_status === 'Active'
  );
  const sameDeptManagers = allManagers.filter(m => m.department === formData.department);
  const otherDeptManagers = allManagers.filter(m => m.department !== formData.department);

  const selectedMgrIds = formData.reporting_manager_ids || [];
  const selectedManagers = selectedMgrIds.map(id => allEmployees.find(e => e.id === id)).filter(Boolean);
  const availableManagers = allManagers.filter(m => !selectedMgrIds.includes(m.id));
  const availableSameDept = availableManagers.filter(m => m.department === formData.department);
  const availableOtherDept = availableManagers.filter(m => m.department !== formData.department);

  const addManager = (mgrId) => {
    if (selectedMgrIds.length < 3 && !selectedMgrIds.includes(mgrId)) {
      handleChange('reporting_manager_ids', [...selectedMgrIds, mgrId]);
    }
    if (selectedMgrIds.length >= 2) setMgrPopoverOpen(false);
  };
  const removeManager = (mgrId) => {
    handleChange('reporting_manager_ids', selectedMgrIds.filter(id => id !== mgrId));
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Employment Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Join Date *</Label>
          <Input 
            type="date" required 
            value={formData.join_date} 
            onChange={(e) => handleChange('join_date', e.target.value)} 
            data-testid="employee-join-date-input"
          />
        </div>

        {/* Position Dropdown with X delete */}
        <div>
          <Label>Position *</Label>
          {!showAddDesignation ? (
            <Popover open={posOpen} onOpenChange={setPosOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-between font-normal" data-testid="employee-position-input">
                  {formData.position || <span className="text-gray-400">Select position</span>}
                  <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <div className="max-h-64 overflow-y-auto">
                  {designations.map((pos) => (
                    <div key={pos} className="flex items-center group hover:bg-gray-100 transition-colors">
                      <button
                        type="button"
                        className="flex-1 text-left px-3 py-2 text-sm"
                        onClick={() => { handleChange('position', pos); setPosOpen(false); }}
                      >
                        <span className="flex items-center gap-2">
                          {formData.position === pos && <Check className="h-3.5 w-3.5 text-blue-600" />}
                          {pos}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="px-2 py-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600"
                        onClick={(e) => { e.stopPropagation(); onHidePosition(pos); }}
                        title={`Remove ${pos}`}
                        data-testid={`delete-pos-${pos}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="border-t p-1">
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-purple-600 font-medium hover:bg-purple-50 rounded flex items-center gap-1"
                    onClick={() => { setPosOpen(false); setShowAddDesignation(true); }}
                    data-testid="add-new-position-btn"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add New Position
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <div className="flex gap-2">
              <Input 
                placeholder="Enter new position"
                value={newDesignation}
                onChange={(e) => setNewDesignation(e.target.value)}
                className="flex-1"
                data-testid="new-designation-input"
              />
              <Button type="button" size="sm" onClick={() => {
                if (newDesignation.trim()) {
                  const normalized = newDesignation.trim().toUpperCase();
                  if (!designations.some(d => d === normalized)) {
                    setDesignations(prev => [...prev, normalized]);
                  }
                  api.post('/api/positions', { position: normalized }).catch(() => {});
                  handleChange('position', normalized);
                  setNewDesignation('');
                  setShowAddDesignation(false);
                }
              }} data-testid="save-new-designation-btn">Add</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => { setShowAddDesignation(false); setNewDesignation(''); }}>Cancel</Button>
            </div>
          )}
        </div>

        {/* Visa Designation (profession on UAE residency visa) */}
        <div>
          <Label>Designation on Visa</Label>
          <Input
            value={formData.visa_designation || ''}
            onChange={(e) => handleChange('visa_designation', e.target.value)}
            placeholder="Profession printed on residency visa (often differs from job title)"
            data-testid="employee-visa-designation-input"
          />
        </div>

        {/* Department Dropdown with X delete */}
        <div>
          <Label>Department *</Label>
          {!showAddDepartment ? (
            <Popover open={deptOpen} onOpenChange={setDeptOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-between font-normal" data-testid="employee-department-select">
                  {formData.department || <span className="text-gray-400">Select department</span>}
                  <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <div className="max-h-64 overflow-y-auto">
                  {departments.map((dept) => (
                    <div key={dept} className="flex items-center group hover:bg-gray-100 transition-colors">
                      <button
                        type="button"
                        className="flex-1 text-left px-3 py-2 text-sm"
                        onClick={() => { handleChange('department', dept); setDeptOpen(false); }}
                      >
                        <span className="flex items-center gap-2">
                          {formData.department === dept && <Check className="h-3.5 w-3.5 text-blue-600" />}
                          {dept}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="px-2 py-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600"
                        onClick={(e) => { e.stopPropagation(); onHideDepartment(dept); }}
                        title={`Remove ${dept}`}
                        data-testid={`delete-dept-${dept}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="border-t p-1">
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-purple-600 font-medium hover:bg-purple-50 rounded flex items-center gap-1"
                    onClick={() => { setDeptOpen(false); setShowAddDepartment(true); }}
                  >
                    <Plus className="h-3.5 w-3.5" /> Add New Department
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <div className="flex gap-2">
              <Input 
                placeholder="Enter new department name"
                value={newDepartment}
                onChange={(e) => setNewDepartment(e.target.value)}
                className="flex-1"
                data-testid="new-department-input"
              />
              <Button type="button" size="sm" onClick={() => {
                if (newDepartment.trim()) {
                  const normalizedDept = newDepartment.trim().toUpperCase();
                  if (!departments.some(d => d === normalizedDept)) {
                    setDepartments(prev => [...prev, normalizedDept]);
                  }
                  handleChange('department', normalizedDept);
                  setNewDepartment('');
                  setShowAddDepartment(false);
                }
              }}>Add</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => { setShowAddDepartment(false); setNewDepartment(''); }}>Cancel</Button>
            </div>
          )}
        </div>

        <div>
          <Label>Employment Status *</Label>
          <Select value={formData.employment_status} onValueChange={onStatusChange}>
            <SelectTrigger data-testid="employee-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Suspended">Suspended</SelectItem>
              <SelectItem value="On leave">On leave</SelectItem>
              <SelectItem value="Resigned">Resigned</SelectItem>
              <SelectItem value="Terminated">Terminated</SelectItem>
              <SelectItem value="Absconding">Absconding</SelectItem>
              <SelectItem value="In Prison">In Prison</SelectItem>
              <SelectItem value="Death">Death</SelectItem>
              <SelectItem value="Visa Cancelled Outside Country">Visa Cancelled Outside Country</SelectItem>
            </SelectContent>
          </Select>
          {!currentEmployeeId && SETTLEMENT_ACTIONS[formData.employment_status] && (
            <p className="text-[11px] text-amber-600 mt-1">Save the employee first, then set this status to record the settlement.</p>
          )}
          <ExitSettlementDialog
            open={!!settleAction}
            action={settleAction}
            employeeId={currentEmployeeId}
            employeeName={formData.name}
            onCancel={() => { setSettleAction(null); handleChange('employment_status', prevStatus); }}
            onDone={(label) => { setSettleAction(null); handleChange('employment_status', label); }}
          />
        </div>
        <div>
          <Label>Employee Role *</Label>
          <Select value={formData.employee_role || 'Staff'} onValueChange={(value) => handleChange('employee_role', value)}>
            <SelectTrigger data-testid="employee-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Owner">Owner</SelectItem>
              <SelectItem value="Top Management">Top Management</SelectItem>
              <SelectItem value="Management">Management</SelectItem>
              <SelectItem value="Manager">Manager</SelectItem>
              <SelectItem value="Staff">Staff</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Reporting Managers <span className="text-xs text-gray-400">(up to 3)</span></Label>
          {/* Selected managers as badges */}
          <div className="flex flex-wrap gap-1.5 mb-1.5 min-h-[28px]">
            {selectedManagers.map((mgr) => (
              <span key={mgr.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-800">
                {mgr.name} <span className="text-[10px] text-blue-500">({mgr.department})</span>
                <button type="button" onClick={() => removeManager(mgr.id)} className="ml-0.5 hover:text-red-600">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          {/* Add manager button/popover */}
          {selectedMgrIds.length < 3 && (
            <Popover open={mgrPopoverOpen} onOpenChange={setMgrPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-full justify-between text-xs h-8" data-testid="add-reporting-manager-btn">
                  <span className="text-gray-500">+ Add reporting manager</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0 max-h-60 overflow-y-auto" align="start">
                {availableSameDept.length > 0 && (
                  <div className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase border-b">Same Department</div>
                )}
                {availableSameDept.map((mgr) => (
                  <button key={mgr.id} type="button" onClick={() => addManager(mgr.id)}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 flex items-center gap-2">
                    <Plus className="h-3 w-3 text-gray-400" />
                    {mgr.name} <span className="text-[10px] text-gray-400">({mgr.position})</span>
                  </button>
                ))}
                {availableOtherDept.length > 0 && (
                  <div className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase border-b border-t">Other Departments</div>
                )}
                {availableOtherDept.map((mgr) => (
                  <button key={mgr.id} type="button" onClick={() => addManager(mgr.id)}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 flex items-center gap-2">
                    <Plus className="h-3 w-3 text-gray-400" />
                    {mgr.name} <span className="text-[10px] text-gray-400">({mgr.position} - {mgr.department})</span>
                  </button>
                ))}
                {availableManagers.length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-400">No managers available</div>
                )}
              </PopoverContent>
            </Popover>
          )}
          {selectedMgrIds.length >= 3 && (
            <p className="text-[10px] text-amber-600 mt-0.5">Maximum 3 reporting managers reached</p>
          )}
        </div>
        <div>
          <Label>LOP Days (Loss of Pay)</Label>
          <Input 
            type="number" min="0" step="0.5"
            value={formData.lop_days} 
            onChange={(e) => handleChange('lop_days', parseFloat(e.target.value) || 0)} 
            data-testid="employee-lop-days-input"
          />
        </div>
      </div>
    </div>
  );
};

export default EmploymentInfoSection;
